<script context="module" lang="ts">
  export var id = 0;
  export const getId = (name) => {
    id += 1;
    return `${name} ${id}`;
  };

  export const defaultSettings = {
    horizontal: false,
    snap: false,
    fullHeight: false,
    autoScroll: false,
    bounceIndication: false,
    height: "auto",
    indicatorBottomPadding: true,
    showIndicator: true,
    indicatorTopPadding: true,
    indicatorColor: "#0284ff",
    style: "",
  };
</script>

<script lang="ts">
  import { setContext, onDestroy } from "svelte";
  import type {
    TComponentContainerSettings,
    TTag,
  } from "../ComponentContainer/types";
  import type { TListSettings, TSettingsProps } from "./types";

  interface $$Props extends TSettingsProps {}

  // Variables
  export const tag: TTag = "List";
  export let settings: Partial<TListSettings> = {};
  export let childrenContainer: Partial<TComponentContainerSettings> = {};
  const variableName = `${getId(tag)}`;

  export var onChange = (index, total) => {};

  // This requires some more testing...
  export var onChangePercentHidden = (index, percentHidden) => {};

  var _settings = {
    ...defaultSettings,
    ...settings,
  };

  // TODO – make this better. This behaves differently than before because
  // now we're always passing in values for every property. Some of those
  // could be explicitly set and some not so it's hard to know which ones
  // we need to override.
  var _childrenContainer = {
    ...childrenContainer,
    width: _settings.horizontal
      ? "calc(100% - var(--sw-container-padding) * 2)"
      : "100%",
    cover: _settings.snap,
    fullWidth: _settings.snap,
    noBackground: _settings.snap,
    height: settings?.fullHeight
      ? "calc(var(--sw-safe-area-height))"
      : _settings.height,
    noMargin: _settings.snap,
  };

  var horizontalContainer;
  var pages;
  var currentIndex = 0;
  var oldIndex = -1;

  const onScroll = () => {
    if (horizontalContainer) {
      currentIndex =
        Math.round(
          (horizontalContainer.scrollLeft / horizontalContainer.scrollWidth) *
            pages
        ) || 0;
      pages = horizontalContainer.childElementCount;

      // Start: Logic for onChangePercentHidden

      // I don't love this...
      let parentElementBoundingRect =
        horizontalContainer.parentElement.getBoundingClientRect();
      let computedScrollWidth =
        parentElementBoundingRect.width * pages -
        parentElementBoundingRect.x * (pages - 1);

      // This would be better but I can't figure out how to get the 6px margin that's being used in the container.
      // let computedScrollWidth = (((horizontalContainer.scrollWidth - parentElementBoundingRect.x) / pages) - 6) * pages;

      let currentIndexForProgress =
        Math.floor(
          (horizontalContainer.scrollLeft / computedScrollWidth) * pages
        ) || 0;
      let percentHidden =
        (horizontalContainer.scrollLeft / computedScrollWidth) * pages -
        currentIndexForProgress;

      // console.log("Left: " + horizontalContainer.scrollLeft);
      // console.log("Width: " + horizontalContainer.scrollWidth);

      onChangePercentHidden(currentIndexForProgress, percentHidden);

      // End: Logic for onChangePercentHidden

      let pageCount = 0;
      var children = horizontalContainer.children;
      for (var i = 0; i < children.length; i++) {
        let child = children[i];
        let display = window
          .getComputedStyle(child)
          .getPropertyValue("display");
        if (display != "none") {
          pageCount += 1;
        }
        pages = pageCount;
      }
      if (oldIndex != currentIndex) {
        oldIndex = currentIndex;
        onChange(currentIndex, pages);
      }
    }
  };

  $: {
    horizontalContainer && onScroll();
  }

  let scrollInterval;
  let autoScrollInterval;

  if (window.self != window.top) {
    scrollInterval = setInterval(onScroll, 200);
  }

  var didTouch = false;

  const onTouchStart = () => {
    didTouch = true;
  };

  const setupAutoScroll = () => {
    autoScrollInterval = setInterval(() => {
      if (horizontalContainer && !didTouch) {
        // get horizontalContainer's Nth child
        // const id = `horizontalContainer-${variableName}`;
        let nextIndex = (currentIndex + 1) % pages;
        let child = horizontalContainer.children[nextIndex];
        child.scrollIntoView({
          behavior: "smooth",
          block: "end",
          inline: "center",
        });
      }
    }, 3000);
  }

  if (_settings.autoScroll && window.self == window.top) {
    setupAutoScroll();
  }

  let didSetAutoScroll = false

  // add a message listener
  window.addEventListener("message", (event) => {
    const data = JSON.parse(event.data);
    if (data.direction == "to_paywall") {
      if (_settings.autoScroll && !didSetAutoScroll) {
        setupAutoScroll();
        didSetAutoScroll = true
      }
    }
  });

  onDestroy(() => {
    clearInterval(scrollInterval);
    clearInterval(autoScrollInterval);
  });

  setContext("prefixContext", `${variableName} > `);
  setContext("containerContext", _childrenContainer);
</script>

{#if _settings.horizontal}
  <div
    class="container"
    class:snap={_settings.snap}
    class:full-height={_settings.fullHeight}
    style={_settings.style}
  >
    <div
      bind:this={horizontalContainer}
      on:scroll={onScroll}
      class:bounce={_settings.bounceIndication && !didTouch}
      on:touchstart={onTouchStart}
      on:mousedown={onTouchStart}
      class="horizontal"
      id="horizontalContainer-{variableName}"
    >
      <!-- <div class="container"> -->
      <slot prefix={variableName} />
      <!-- </div> -->
    </div>
    <div
      class="indicators"
      class:padding-b={_settings.indicatorBottomPadding}
      class:padding-t={_settings.indicatorTopPadding}
    >
      {#if horizontalContainer && _settings.showIndicator}
        {#each Array(pages) as _, i}
          <div
            class="indicator"
            style="background-color: {_settings.indicatorColor};"
            class:active={i == currentIndex}
          >
            .
          </div>
        {/each}
      {/if}
    </div>
  </div>
{:else}
  <div style={_settings.style}>
    <slot prefix={variableName} />
  </div>
{/if}

<style>
  .container {
    position: relative;
  }

  .horizontal {
    position: relative;
    display: flex;
    overflow-x: scroll;
    flex-flow: row nowrap;
    flex-shrink: 0;
    gap: var(--sw-spacing);
    padding-right: var(--sw-container-padding);
    padding-left: var(--sw-container-padding);
    align-items: stretch;
    justify-content: stretch !important;
    width: calc(100% + 2 * var(--sw-container-padding));
    left: 50%;
    transform: translate(-50%, 0);
  }

  .indicators {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    display: none;
    position: absolute;
    bottom: 0px;
    right: 0px;
  }

  .indicators.padding-t {
    padding-top: var(--sw-container-padding);
  }

  .indicators.padding-b {
    padding-bottom: var(--sw-container-padding);
  }

  .snap .indicators {
    display: flex;
  }

  .indicator {
    border-radius: 100%;
    height: 7px;
    width: 7px;
    flex-shrink: 0;
    opacity: 0.25;
    transition: opacity 200ms ease-in-out;
    color: rgba(0, 0, 0, 0);
    white-space: nowrap;
    filter: grayscale(1);
  }

  .indicator.active {
    opacity: 0.7;
    filter: none;
  }

  .snap .horizontal {
    scroll-snap-type: x mandatory;
    scroll-snap-type: proximity;
  }

  :global(.bounce > *) {
    animation: bounce 4s ease-in-out infinite;
  }
  
@keyframes bounce {
  0%, 100% {
    transform: translateX(-50%);
  }
  15% {
    transform: translateX(-80%);
  }
  25% {
    transform: translateX(-48%);
  }
  32% {
    transform: translateX(-50%);
  }

}

</style>
